import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { twMerge } from 'tailwind-merge';
import PathfinderDropdownSelectBar from './PathfinderDropdownSelectBar';
import PathfinderSingleSelectOption from '../option/PathfinderSingleSelectOption';

const DEFAULT_PLACEHOLDER = 'Please select 000';
const DEFAULT_VISIBLE_OPTION_COUNT = 4;
const OPTION_ROW_HEIGHT = 38;
const OPTION_GAP = 0;
const PANEL_PADDING = 16;

const normalizeOption = (option, index) => {
  if (typeof option === 'string') {
    return {
      value: `option-${index + 1}`,
      label: option,
    };
  }

  return {
    value: option?.value ?? `option-${index + 1}`,
    label: option?.label ?? '',
  };
};

/**
 * @example
 * <PathfinderSingleSelectDropdown
 *   placeholder="Please select 000"
 *   options={['Ghana', 'Gabon', 'Guyana']}
 * />
 *
 * @example
 * <PathfinderSingleSelectDropdown
 *   value={country}
 *   onChange={(nextValue) => setCountry(nextValue)}
 *   options={[
 *     { value: 'ghana', label: 'Ghana' },
 *     { value: 'gabon', label: 'Gabon' },
 *   ]}
 *   menuWidth="full"
 * />
 */
function PathfinderSingleSelectDropdown({
  options = [],
  value,
  defaultValue = '',
  onChange,
  open,
  defaultOpen = false,
  onOpenChange,
  placeholder = DEFAULT_PLACEHOLDER,
  visibleOptionCount = DEFAULT_VISIBLE_OPTION_COUNT,
  menuWidth = 'full',
  className = '',
  menuClassName = '',
  triggerClassName = '',
}) {
  const rootRef = useRef(null);
  const [internalValue, setInternalValue] = useState(defaultValue);
  const [internalOpen, setInternalOpen] = useState(defaultOpen);

  const normalizedOptions = useMemo(
    () => options.map(normalizeOption).filter((option) => option.label),
    [options],
  );

  const isControlledValue = value !== undefined;
  const selectedValue = isControlledValue ? value : internalValue;
  const isControlledOpen = open !== undefined;
  const isOpen = isControlledOpen ? open : internalOpen;

  const selectedOption = normalizedOptions.find(
    (option) => option.value === selectedValue,
  );
  const hasSelectedValue = Boolean(selectedOption);
  const shouldScroll = normalizedOptions.length > visibleOptionCount;
  const panelMaxHeight =
    visibleOptionCount * OPTION_ROW_HEIGHT +
    Math.max(0, visibleOptionCount - 1) * OPTION_GAP +
    PANEL_PADDING;

  const setOpenState = useCallback(
    (nextOpen) => {
      if (!isControlledOpen) {
        setInternalOpen(nextOpen);
      }

      onOpenChange?.(nextOpen);
    },
    [isControlledOpen, onOpenChange],
  );

  const handleToggle = () => {
    setOpenState(!isOpen);
  };

  const handleSelect = (option) => {
    if (!isControlledValue) {
      setInternalValue(option.value);
    }

    onChange?.(option.value, option);
    setOpenState(false);
  };

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpenState(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
    };
  }, [setOpenState]);

  return (
    <div ref={rootRef} className={twMerge('relative w-full', className)}>
      <PathfinderDropdownSelectBar
        value={selectedOption?.label ?? ''}
        placeholder={placeholder}
        state={isOpen ? 'active' : hasSelectedValue ? 'selected' : 'default'}
        onClick={handleToggle}
        className={twMerge('max-w-none w-full', triggerClassName)}
      />

      {isOpen ? (
        <div
          className={twMerge(
            'absolute left-0 top-[calc(100%+6px)] z-20 overflow-hidden rounded-[15px] bg-grayscale-gray00 shadow-popup-02',
            menuWidth === 'fit' ? 'min-w-full w-fit' : 'w-full',
            shouldScroll
              ? 'border border-grayscale-gray02'
              : 'border border-grayscale-gray01',
            menuClassName,
          )}
        >
          <div
            role='listbox'
            tabIndex={-1}
            aria-activedescendant={
              selectedOption
                ? `pathfinder-option-${selectedOption.value}`
                : undefined
            }
            className={twMerge(
              'flex flex-col gap-0 p-2',
              shouldScroll
                ? 'max-h-[240px] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:#d5d2cd_rgba(249,247,244,0.4)] [&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:bg-[rgba(249,247,244,0.4)] [&::-webkit-scrollbar-track]:rounded-r-[8px] [&::-webkit-scrollbar-thumb]:bg-grayscale-gray03 [&::-webkit-scrollbar-thumb]:rounded-[10px]'
                : '',
            )}
            style={
              shouldScroll ? { maxHeight: `${panelMaxHeight}px` } : undefined
            }
          >
            {normalizedOptions.map((option) => {
              const isSelected = option.value === selectedValue;

              return (
                <PathfinderSingleSelectOption
                  key={option.value}
                  id={`pathfinder-option-${option.value}`}
                  label={option.label}
                  state={isSelected ? 'selected' : 'default'}
                  reserveCheckSlot={hasSelectedValue}
                  onClick={() => handleSelect(option)}
                  className={menuWidth === 'fit' ? 'min-w-fit' : 'w-full'}
                />
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default PathfinderSingleSelectDropdown;
